import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CloseIcon } from "../../../assets/icons";
import Loader from "../../Loader/Loader";
import { CloseButton, Actions, Btn } from "./SendToVAModalStyled";
import {
  ReviewDrawer,
  DrawerTitle,
  PageLink,
  ReviewBody,
  FormLine,
  Headline,
  Group,
  GroupTitle,
  Item,
  Mark,
  ItemBody,
  Snippet,
  Small,
  PassList,
  OverrideBox,
  OverrideLabel,
  ReasonInput,
  Notice,
  SubmitButton,
} from "./VaPageReviewModalStyled";

const MARKS = { stop: "!", warn: "!", pass: "✓", info: "i" };

/**
 * The page review step of Submit to VA (Matthew, 2026-10-30): after the
 * veteran details pass, WHG Docs looks at the pages — which VA form this is,
 * whether every required signature box is signed and dated, whether the
 * veteran named on the form is the one VA will be told about, whether the
 * pages are readable — and shows what it found before the send.
 *
 * `review` is the server's payload (null while loading): `stops` block the
 * send, `warnings` are for the eye, `passes` are what was checked. Anyone
 * may fix the document and submit again, or tick "Send anyway" and give a
 * reason; the reason goes into the submission's history (Matthew,
 * 2026-10-30: "Let the user fix it or send anyway"). A server that answers
 * `applies: false` (the review is not switched on for this person) gets the
 * plain confirmation.
 *
 * It is a panel docked on the right, not a window over the page (Matthew,
 * 2026-11-01: "I need to be able to see the document while I'm reviewing
 * these"): the open document stays visible beside it, and a finding's page
 * link scrolls the viewer to that page (`onJumpToPage`).
 */
export default function VaPageReviewModal({
  fileName,
  copyNote,
  review,
  loading,
  loadError,
  busy,
  refusal,
  onBack,
  onClose,
  onSubmit,
  onJumpToPage,
}) {
  const [sendAnyway, setSendAnyway] = useState(false);
  const [reason, setReason] = useState("");

  const applies = review?.applies !== false;
  const stops = applies ? review?.stops || [] : [];
  const warnings = applies ? review?.warnings || [] : [];
  const passes = applies ? review?.passes || [] : [];
  const infos = applies ? review?.info || [] : [];
  const enforced = review?.enforced !== false;
  const blocked = enforced && stops.length > 0;
  const canOverride = review?.can_override !== false;
  const minReason = review?.override_reason_min_chars || 10;
  const reasonOk = reason.trim().length >= minReason;

  // A fresh review (the file or details changed) starts the override over.
  useEffect(() => {
    setSendAnyway(false);
    setReason("");
  }, [review]);

  const overriding = blocked && canOverride && sendAnyway;
  const submitDisabled =
    Boolean(busy) || Boolean(loading) || (blocked && !(overriding && reasonOk));

  const tone = stops.length ? "stop" : warnings.length ? "warn" : "pass";
  const ai = review?.ai || {};

  const handleSubmit = () => {
    if (submitDisabled) return;
    onSubmit(overriding ? { override: true, reason: reason.trim() } : { override: false, reason: "" });
  };

  const canJump = typeof onJumpToPage === "function";
  const jump = (page) => {
    if (canJump && Number.isInteger(page) && page > 0) onJumpToPage(page);
  };

  const renderItem = (finding, itemTone, key) => (
    <Item key={key} $tone={itemTone} data-testid={`page-review-${itemTone}`}>
      <Mark $tone={itemTone}>{MARKS[itemTone]}</Mark>
      <ItemBody>
        <div>{finding.message}</div>
        {finding.source === "ai" && <Small>From the AI look at the pages.</Small>}
        {finding.snippet && (
          <Snippet
            src={finding.snippet}
            alt={`Page ${finding.page || ""} — ${finding.code}`}
            $clickable={canJump && Boolean(finding.page)}
            onClick={() => jump(finding.page)}
          />
        )}
        {canJump && Number.isInteger(finding.page) && finding.page > 0 && (
          <PageLink type="button" onClick={() => jump(finding.page)} data-testid="page-review-jump">
            Show me page {finding.page}
          </PageLink>
        )}
      </ItemBody>
    </Item>
  );

  return (
    <ReviewDrawer role="complementary" aria-label="Page review before sending to Veterans Affairs" data-testid="page-review-drawer">
        {(busy || loading) && <Loader />}
        <CloseButton onClick={onClose} disabled={Boolean(busy)} aria-label="Close">
          <CloseIcon />
        </CloseButton>
        <DrawerTitle>Before it goes to Veterans Affairs</DrawerTitle>

        <ReviewBody>
          <FormLine>
            {fileName}
            {review?.form_number && (
              <>
                {" · "}VA Form {review.form_number}
                {review.revision ? ` (${review.revision})` : ""}
              </>
            )}
            {review?.page_count ? ` · ${review.page_count} page${review.page_count === 1 ? "" : "s"}` : ""}
          </FormLine>
          {copyNote && <Small data-testid="page-review-copy-note">{copyNote}</Small>}

          {loading && <Headline $tone="pass">Looking at the pages…</Headline>}

          {!loading && loadError && (
            <Notice data-testid="page-review-load-error">
              The page review could not run here ({loadError}). WHG Docs will still check the pages before it sends.
            </Notice>
          )}

          {!loading && review && !applies && (
            <Headline $tone="pass" data-testid="page-review-headline">
              File has passed the metadata checks. Send it to Veterans Affairs?
            </Headline>
          )}

          {!loading && review && applies && (
            <>
              <Headline $tone={tone} data-testid="page-review-headline">
                {review.headline}
              </Headline>

              {stops.length > 0 && (
                <Group>
                  <GroupTitle>Fix before sending</GroupTitle>
                  {stops.map((f, i) => renderItem(f, "stop", `stop-${i}`))}
                </Group>
              )}

              {warnings.length > 0 && (
                <Group>
                  <GroupTitle>Look before sending</GroupTitle>
                  {warnings.map((f, i) => renderItem(f, "warn", `warn-${i}`))}
                </Group>
              )}

              {passes.length > 0 && (
                <Group>
                  <GroupTitle>Checked</GroupTitle>
                  <PassList data-testid="page-review-passes">
                    {passes.map((f, i) => (
                      <li key={`pass-${i}`}>{f.message}</li>
                    ))}
                  </PassList>
                </Group>
              )}

              {(infos.length > 0 || ai.summary || ai.skipped) && (
                <Group>
                  <Small>
                    {ai.ran && ai.summary ? `The AI look: ${ai.summary}` : null}
                    {!ai.ran && ai.skipped ? `AI look not run: ${ai.skipped}.` : null}
                    {infos.map((f, i) => (
                      <div key={`info-${i}`}>{f.message}</div>
                    ))}
                  </Small>
                </Group>
              )}

              {blocked && canOverride && (
                <OverrideBox data-testid="page-review-override">
                  <Small>Fix the document and submit again, or:</Small>
                  <OverrideLabel>
                    <input
                      type="checkbox"
                      checked={sendAnyway}
                      onChange={(e) => setSendAnyway(e.target.checked)}
                      disabled={Boolean(busy)}
                    />
                    Send anyway — I have looked at this
                  </OverrideLabel>
                  {sendAnyway && (
                    <>
                      <ReasonInput
                        placeholder="Why it goes despite the stop (a few words). This is written into the submission's history."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={Boolean(busy)}
                        aria-label="Reason for sending anyway"
                      />
                      {!reasonOk && (
                        <Small>At least {minReason} characters.</Small>
                      )}
                    </>
                  )}
                </OverrideBox>
              )}
            </>
          )}

          {refusal && <Notice data-testid="page-review-refusal">{refusal}</Notice>}
        </ReviewBody>

        <Actions>
          <Btn onClick={onBack} disabled={Boolean(busy)}>
            Back
          </Btn>
          <SubmitButton onClick={handleSubmit} disabled={submitDisabled} data-testid="page-review-submit">
            {busy ? "Sending…" : overriding ? "Send anyway" : "Submit"}
          </SubmitButton>
        </Actions>
    </ReviewDrawer>
  );
}

VaPageReviewModal.propTypes = {
  fileName: PropTypes.string,
  copyNote: PropTypes.string,
  review: PropTypes.shape({
    applies: PropTypes.bool,
    ready: PropTypes.bool,
    headline: PropTypes.string,
    form_number: PropTypes.string,
    revision: PropTypes.string,
    page_count: PropTypes.number,
    stops: PropTypes.array,
    warnings: PropTypes.array,
    passes: PropTypes.array,
    info: PropTypes.array,
    ai: PropTypes.object,
    enforced: PropTypes.bool,
    can_override: PropTypes.bool,
    override_reason_min_chars: PropTypes.number,
  }),
  loading: PropTypes.bool,
  loadError: PropTypes.string,
  busy: PropTypes.bool,
  refusal: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onJumpToPage: PropTypes.func,
};
